import { accessToken, clearSession } from './session.js';

/**
 * Cliente HTTP.
 *
 * Sempre caminhos relativos (`/api/...`): front e back são a MESMA ORIGEM, pelo
 * proxy do nginx em produção e do Vite em dev. É isso que permite o refresh
 * token viver num cookie `httpOnly` e dispensa CORS.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Token do telão, para as rotas de display. */
  displayToken?: string;
  /** Ativa a idempotência do comando (ver decisão 9). */
  idempotencyKey?: string;
  /** Rotas públicas (pareamento) não devem disparar renovação de sessão. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;

  if (options.displayToken) {
    headers.authorization = `Bearer ${options.displayToken}`;
  } else if (!options.anonymous) {
    const token = await accessToken();
    if (!token) throw new ApiError(401, 'AUTH_REQUIRED', 'Entre novamente para continuar.');
    headers.authorization = `Bearer ${token}`;
  }

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const payload = (await parse(response)) as {
    error?: { code?: string; message?: string; details?: unknown };
  } | null;

  if (!response.ok) {
    const error = payload?.error;

    // Sessão morreu de verdade (não é só token expirado, que o session.ts já
    // renova): limpa para a UI mandar entrar de novo.
    if (response.status === 401 && !options.displayToken && !options.anonymous) clearSession();

    throw new ApiError(
      response.status,
      error?.code ?? 'REQUEST_FAILED',
      error?.message ?? 'Não foi possível completar a ação.',
      error?.details,
    );
  }

  return payload as T;
}

/** Upload da foto do ganhador. Devolve a CHAVE, nunca uma URL. */
export async function uploadPhoto(eventId: string, file: Blob): Promise<string> {
  const token = await accessToken();
  if (!token) throw new ApiError(401, 'AUTH_REQUIRED', 'Entre novamente para continuar.');

  const form = new FormData();
  form.append('photo', file, 'foto.jpg');

  const response = await fetch(`/api/events/${eventId}/photos`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` },
    credentials: 'same-origin',
    body: form,
  });

  const payload = (await parse(response)) as { photoKey?: string; error?: { message?: string } };

  if (!response.ok || !payload?.photoKey) {
    throw new ApiError(
      response.status,
      'PHOTO_UPLOAD',
      payload?.error?.message ?? 'Não foi possível enviar a foto.',
    );
  }

  return payload.photoKey;
}

/**
 * A `Idempotency-Key` NÃO é montada aqui a partir do estado — ver o comentário
 * de `lib/commands.ts`, que explica o bug que isso causava. Use `sendCommand`.
 */
